import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Send, X } from "lucide-react";
import { assistantChat } from "@/lib/api";
import { ASSISTANT } from "@/constants/testIds";

export default function AIChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hola, soy ASIROX AI. Cuéntame qué tipo de centro tienes y te recomendaré el equipo ideal en segundos.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const res = await assistantChat(sessionId, text);
      setSessionId(res.session_id);
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Lo siento, hubo un error. Intenta de nuevo o escríbenos a info@asirox.com." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid={ASSISTANT.open_widget}
          onClick={() => setOpen(true)}
          className="fixed bottom-6 left-6 md:left-auto md:right-6 z-[60] group"
          aria-label="Abrir asistente IA"
        >
          <span className="absolute inset-0 rounded-full bg-[#ff9c00] blur-xl opacity-50 group-hover:opacity-80 transition" />
          <span className="relative flex items-center gap-2 px-5 py-3.5 bg-black border border-white/30 text-white font-bold">
            <Sparkles size={16} strokeWidth={1.8} className="text-white" />
            <span className="text-sm">Asistente IA</span>
          </span>
        </button>
      )}

      {open && (
        <div data-testid={ASSISTANT.panel} className="fixed bottom-6 left-6 right-6 md:left-auto md:right-6 z-[60] w-auto md:w-[92vw] md:max-w-md bg-white border border-[#ff9c00]/40 flex flex-col shadow-2xl" style={{ height: "min(620px, 80vh)" }}>
          <div className="flex items-center justify-between px-5 py-4 border-b border-black/10 bg-[#f7ebd9]">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#ff9c00] animate-pulse" />
              <p className="font-heading font-bold ink-heading">ASIROX AI</p>
              <span className="overline ml-2">Claude Sonnet 4.5</span>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Cerrar" className="text-black/60 hover:text-black">
              <X size={18}/>
            </button>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {messages.map((m, i) => (
              <div key={i} data-testid={ASSISTANT.message(i)} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`chat-bubble max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap rounded-sm ${
                  m.role === "user"
                    ? "bg-[#ff9c00] text-black font-semibold"
                    : "ai-bubble text-black"
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="ai-bubble chat-bubble text-black/80 px-4 py-3 text-sm flex gap-1 rounded-sm">
                  <span className="w-1.5 h-1.5 bg-[#ff9c00] rounded-full animate-bounce" style={{ animationDelay: "0ms" }}/>
                  <span className="w-1.5 h-1.5 bg-[#ff9c00] rounded-full animate-bounce" style={{ animationDelay: "120ms" }}/>
                  <span className="w-1.5 h-1.5 bg-[#ff9c00] rounded-full animate-bounce" style={{ animationDelay: "240ms" }}/>
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-black/10 flex gap-2">
            <input
              data-testid={ASSISTANT.input}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder="Pregúntame qué equipo te conviene..."
              className="flex-1 bg-[#f7ebd9] border border-black/15 px-4 py-3 text-sm text-black placeholder:text-black/40 focus:border-[#ff9c00] outline-none"
            />
            <button
              data-testid={ASSISTANT.send}
              onClick={send}
              disabled={loading || !input.trim()}
              className="px-4 bg-[#ff9c00] text-black font-bold disabled:opacity-40"
              aria-label="Enviar"
            >
              <Send size={16}/>
            </button>
          </div>
          <div className="px-4 pb-3">
            <Link to="/asesor-dermo-facial-ia" className="text-xs text-black/60 hover:text-[#ff9c00] link-underline">
              Abrir asistente en pantalla completa →
            </Link>
          </div>
        </div>
      )}
    </>
  );
}
